import type { ObjectMetadata } from '@olytic/foundation';

import { OBJECT_DEFS } from '../../objects/objectDefs';
import {
  activityColumns, asDate, asLabel, asCheck, column, createdByField, documentColumns, lastModifiedByField, listColumnsOf, membershipColumns,
  railMeetingColumns, related, type Row, schemaRecord, text, urlField,
} from './kit';

const def = OBJECT_DEFS.contacts;

/**
 * Its fields come from the database (the server's SCHEMA_OBJECTS), shown by
 * their type, the Account linked to its record; any column not placed below
 * appears in Other Fields (MOTHER-295).
 */
const field = schemaRecord('contacts');

const details = [
  field('first_name', 'First Name'),
  field('last_name', 'Last Name'),
  field('title', 'Title'),
  field('account_name', 'Account'),
  field('is_primary_contact', 'Primary Contact'),
  field('source', 'Source'),
  field('email', 'Email'),
  urlField('linkedin_url', 'LinkedIn'),
  field('is_linkedin_connection', 'LinkedIn Connection'),
  field('phone', 'Phone'),
  field('log_texts_calls', 'Log Texts & Calls'),
  field('warmth', 'Warmth'),
  field('created_at', 'Created'),
  field('apollo_contact_id', 'Apollo Contact ID'),
  createdByField(),
  lastModifiedByField(),
];

/** A contact: a person at an account. Rationale: contacts.md. */
export const contacts: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Contact',
  plural: def.label,
  icon: def.icon,
  listColumns: listColumnsOf(def, details),
  details,
  sections: [
    { key: 'identity', fields: ['first_name', 'last_name', 'title', 'account_name', 'is_primary_contact', 'source'] },
    { key: 'reach', label: 'Reach', fields: ['email', 'phone', 'linkedin_url', 'is_linkedin_connection', 'log_texts_calls', 'warmth'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['created_at', 'created_by_name', 'last_modified_by_name', 'apollo_contact_id'] },
  ],
  related: [
    related('opportunities', 'Opportunities', 'opportunities', [
      column('name', 'Opportunity', { primary: true }),
      column('stage', 'Stage', { format: asLabel }),
      column('role', 'Role', { format: asLabel }),
      { ...column('is_primary', 'Primary'), render: (row) => asCheck(row.is_primary) },
      column('close_date', 'Close Date', { format: asDate }),
    ]),
    related('meetings', 'Meetings', 'meetings', railMeetingColumns(column('role', 'Role', { format: asLabel }))),
    related('activities', 'Activities', 'activities', activityColumns().slice(0, 3)),
    related('audiences', 'Audiences', 'audiences', membershipColumns().slice(0, 3)),
    related('documents', 'Documents', 'documents', [documentColumns()[0]!, documentColumns()[2]!]),
  ],
  tabs: [
    { key: 'information', label: 'Information', details: true },
    // Activity (the timeline) sits between these two; ObjectRecordView adds it.
    { key: 'related', label: 'Related', related: ['opportunities', 'meetings', 'activities', 'audiences', 'documents'] },
  ],
  titleOf: (row) => text(row.name),
  searchKeys: ['first_name', 'last_name', 'email', 'title', 'phone'],
};
